import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable } from 'rxjs';
import { B2BPartnerListResponse } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-partner-list-response';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { B2BPartnerService } from '@api/services/b-2-b-partner.service';
import { tap } from 'rxjs/operators';
import { B2BPartnerAddRequest } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-partner-add-request';
import { B2BVisOrgListResponse } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-vis-org-list-response';
import { B2BPartnerSetRequest } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-partner-set-request';
import { CustomRxState } from '@core/state/rx.state';


interface IPartnersState {
    loading: Record<StartWithUnit, boolean>;
    partners: B2BPartnerListResponse[];
    organizations: B2BVisOrgListResponse[];
}

interface IPartnersAction {
    requestPartners: B2BPartnerListResponse[];
    requestOrganizations: B2BVisOrgListResponse[];
}

@Injectable()
export class PartnersState extends CustomRxState<IPartnersState> {
    readonly #b2BPartnerService: B2BPartnerService = inject(B2BPartnerService);
    readonly #actions: RxActions<IPartnersAction> = new RxActionFactory<IPartnersAction>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly partners$: Observable<B2BPartnerListResponse[]> = this.select('partners');
    public readonly organizations$: Observable<B2BVisOrgListResponse[]> = this.select('organizations');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestPartners();
        this.requestOrganizations();
    }

    public requestOrganizations(): void {
        this.hold(
            this.#b2BPartnerService.apiB2BB2BVisOrgListGet(),
            this.#actions.requestOrganizations,
        );
    }

    public requestPartners(): void {
        this.hold(
            this.#b2BPartnerService.apiB2BB2BPartnerListGet().pipe(
                startWithLoading(this, StartWithUnit.RequestPartners),
            ),
            this.#actions.requestPartners,
        );
    }

    public addPartner(body: B2BPartnerAddRequest): Observable<unknown> {
        return this.#b2BPartnerService.apiB2BB2BPartnerAddPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditPartner),
            tap(() => {
                this.requestPartners();
                this.requestOrganizations();
            }),
        );
    }

    public editPartner(body: B2BPartnerSetRequest): Observable<unknown> {
        return this.#b2BPartnerService.apiB2BB2BPartnerSetPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditPartner),
            tap(() => {
                this.requestPartners();
                this.requestOrganizations();
            }),
        );
    }

    public deletePartner(orgId: number): Observable<unknown> {
        return this.#b2BPartnerService.apiB2BB2BPartnerRemoveDelete({ orgId }).pipe(
            startWithLoading(this, StartWithUnit.DeletePartner),
            tap(() => {
                this.requestPartners();
                this.requestOrganizations();
            }),
        );
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            partners: null,
            organizations: null,
        });
    }

    private connectSelectors(): void {
        this.connect('partners', this.#actions.requestPartners$);
        this.connect('organizations', this.#actions.requestOrganizations$);
    }
}
